import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { debounceTime, distinctUntilChanged, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import {
  ErpLeaveImportApprovedRequest,
  ErpLeaveImportLeaveRecordRow,
  ErpLeaveImportLeaveRecordsParams,
  ErpLeaveImportLeaveRecordsResponse,
  ErpLeaveImportResultDetail,
  ErpLeaveImportResultRow,
  ErpLeaveImportResultsResponse,
  ErpLeaveImportRowResult,
  ErpLeaveImportService,
} from 'src/app/services/api/erp-leave-import/erp-leave-import.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { FormImportNewComponent } from 'src/app/pages/import/components/form-import-new/form-import-new.component';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import {
  createDatatableSortParams,
  Datatable,
  DatatableSortChangeEvent,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { DatatableComponent } from 'src/shared/datatable/datatable.component';
import { SharedModule } from 'src/shared/shared.module';

declare const bootstrap: any;

interface ErpLeaveImportTeacherDataSource extends Datatable {
  data: ErpLeaveImportResultRow[];
  offset: number;
}

interface ErpLeaveImportLeaveRecordDataSource extends Datatable {
  data: ErpLeaveImportLeaveRecordRow[];
  offset: number;
}

type ErpLeaveImportTab = 'leave_records' | 'match_results';
type ErpLeaveImportDetailStatusTab = 'approved' | 'pending';
type ErpLeaveImportComparisonStatus = 'matched' | 'missing_erp' | 'not_approved' | 'missing_approved_request';

interface ErpLeaveImportComparisonRow {
  id: string;
  sequence: number;
  systemRequest: ErpLeaveImportApprovedRequest | null;
  erpRequest: ErpLeaveImportRowResult | null;
  systemLeaveDate: string | null;
  sortDate: string | null;
  comparisonStatus: ErpLeaveImportComparisonStatus;
  comparisonLabel: string;
}

interface ErpLeaveImportComparisonDataSource extends Datatable {
  data: ErpLeaveImportComparisonRow[];
  offset: number;
}

@Component({
  selector: 'app-erp-leave-import',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
    FormImportNewComponent,
    ConfirmDialogComponent,
  ],
  templateUrl: './erp-leave-import.component.html',
  styleUrl: './erp-leave-import.component.scss',
})
export class ErpLeaveImportComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('teacherInfoTemplate', { static: true })
  teacherInfoTemplate!: TemplateRef<any>;
  @ViewChild('matchCountTemplate', { static: true })
  matchCountTemplate!: TemplateRef<any>;
  @ViewChild('systemRequestUnmatchedCountTemplate', { static: true })
  systemRequestUnmatchedCountTemplate!: TemplateRef<any>;
  @ViewChild('systemRequestCountTemplate', { static: true })
  systemRequestCountTemplate!: TemplateRef<any>;
  @ViewChild('importedAtTemplate', { static: true })
  importedAtTemplate!: TemplateRef<any>;
  @ViewChild('leaveDateTemplate', { static: true })
  leaveDateTemplate!: TemplateRef<any>;
  @ViewChild('erpLeaveStartDateTemplate', { static: true })
  erpLeaveStartDateTemplate!: TemplateRef<any>;
  @ViewChild('erpLeaveEndDateTemplate', { static: true })
  erpLeaveEndDateTemplate!: TemplateRef<any>;
  @ViewChild('erpSubmittedAtTemplate', { static: true })
  erpSubmittedAtTemplate!: TemplateRef<any>;
  @ViewChild('erpLastUpdatedAtTemplate', { static: true })
  erpLastUpdatedAtTemplate!: TemplateRef<any>;
  @ViewChild('leaveTypeTemplate', { static: true })
  leaveTypeTemplate!: TemplateRef<any>;
  @ViewChild('usageStatusTemplate', { static: true })
  usageStatusTemplate!: TemplateRef<any>;
  @ViewChild('teacherActionTemplate', { static: true })
  teacherActionTemplate!: TemplateRef<any>;
  @ViewChild('comparisonIndexTemplate', { static: true })
  comparisonIndexTemplate!: TemplateRef<any>;
  @ViewChild('comparisonSystemRequestTemplate', { static: true })
  comparisonSystemRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonErpRequestTemplate', { static: true })
  comparisonErpRequestTemplate!: TemplateRef<any>;
  @ViewChild('comparisonLeaveDateTemplate', { static: true })
  comparisonLeaveDateTemplate!: TemplateRef<any>;
  @ViewChild('comparisonMakeupTeachingTemplate', { static: true })
  comparisonMakeupTeachingTemplate!: TemplateRef<any>;
  @ViewChild('comparisonStatusTemplate', { static: true })
  comparisonStatusTemplate!: TemplateRef<any>;
  @ViewChild(FormImportNewComponent)
  importForm?: FormImportNewComponent;
  @ViewChild('exportErrorDialog')
  exportErrorDialog?: ConfirmDialogComponent;
  @ViewChild('importSuccessDialog')
  importSuccessDialog?: ConfirmDialogComponent;
  @ViewChild('importExistingDataConfirmDialog')
  importExistingDataConfirmDialog?: ConfirmDialogComponent;
  @ViewChild('importCheckErrorDialog')
  importCheckErrorDialog?: ConfirmDialogComponent;
  @ViewChildren(DatatableComponent)
  datatables?: QueryList<DatatableComponent>;

  isImporting = false;
  isExporting = false;
  isCheckingExistingData = false;
  isLoadingDetail = false;
  isTeacherDetailTableReady = signal(false);
  lastAction = signal<'export' | 'import' | null>(null);
  activeTab = signal<ErpLeaveImportTab>('leave_records');
  activeDetailStatusTab = signal<ErpLeaveImportDetailStatusTab>('approved');
  tableLoading = signal(false);
  leaveRecordTableLoading = signal(false);
  exportErrorMessage = 'ไม่สามารถดาวน์โหลดแบบฟอร์มได้ กรุณาลองใหม่อีกครั้ง';
  form!: FormGroup;
  selectedTeacherSummary?: ErpLeaveImportResultDetail;
  columns: any[] = [];
  leaveRecordColumns: any[] = [];
  comparisonColumns: any[] = [];
  readonly datatableColumnMode = ColumnMode;
  pageSizeOptions = [10, 20, 50];
  comparisonPageSizeOptions = [ 10, 20, 50];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  teacherTableDatasource: ErpLeaveImportTeacherDataSource = this.createTeacherTableDatasource();
  leaveRecordTableDatasource: ErpLeaveImportLeaveRecordDataSource = this.createLeaveRecordTableDatasource();
  comparisonTableDatasource: ErpLeaveImportComparisonDataSource = this.createComparisonTableDatasource();

  private erpLeaveImportService = inject(ErpLeaveImportService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private studyPeriodService = inject(StudyPeriodService);
  private cdr = inject(ChangeDetectorRef, { optional: true });
  private academicTerms: AcademicTermYearModel[] = [];
  private currentPage = 1;
  private currentLimit = 10;
  private leaveRecordCurrentPage = 1;
  private leaveRecordCurrentLimit = 10;
  private comparisonCurrentPage = 1;
  private comparisonCurrentLimit = 10;
  private tableRefreshTimers = new Set<ReturnType<typeof setTimeout>>();
  private currentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private leaveRecordCurrentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private readonly sortFieldMap = {
    index: 'imported_at',
    teacher: 'full_name',
    system_matched_request_count: 'system_matched_request_count',
    system_unmatched_request_count: 'system_unmatched_request_count',
    system_request_count: 'system_request_count',
    imported_at: 'imported_at',
  };
  private readonly leaveRecordSortFieldMap = {
    sequence: 'erp_leave_start_date',
    erp_person_number: 'erp_person_number',
    erp_person_name: 'erp_person_name',
    erp_full_name: 'erp_full_name',
    erp_department: 'erp_department',
    leave_type: 'leave_type',
    erp_leave_start_date: 'erp_leave_start_date',
    erp_leave_end_date: 'erp_leave_end_date',
    erp_submitted_at: 'erp_submitted_at',
    erp_last_updated_at: 'erp_last_updated_at',
    erp_approval_status: 'erp_approval_status',
    erp_adjustment_status: 'erp_adjustment_status',
  };

  get hasOnlyMatchedRows(): boolean {
    if (this.selectedTeacherSummary == null) {
      return false;
    }

    return this.selectedTeacherSummary.import_unmatched_count === 0
      && this.selectedTeacherSummary.system_unmatched_count === 0;
  }

  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.initLeaveRecordColumns();
    this.initComparisonColumns();
    this.listenLeaveRecordFilters();
    this.loadAcademicTermOptions();
  }

  ngOnDestroy(): void {
    this.tableRefreshTimers.forEach((timer) => clearTimeout(timer));
    this.tableRefreshTimers.clear();
  }

  onExportExcel(): void {
    this.lastAction.set('export');

    if (this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.erpLeaveImportService.exportTemplate().subscribe({
      next: (blob) => this.downloadBlob(blob, 'erp_leave_template.xlsx'),
      error: (err) => {
        this.exportErrorMessage = this.getErrorMessage(err, 'ไม่สามารถดาวน์โหลดแบบฟอร์มได้ กรุณาลองใหม่อีกครั้ง');
        this.exportErrorDialog?.open();
        this.isExporting = false;
      },
      complete: () => {
        this.isExporting = false;
      },
    });
  }

  onImport(): void {
    this.lastAction.set('import');

    if (this.isImporting || this.isCheckingExistingData) {
      return;
    }

    this.isCheckingExistingData = true;
    this.erpLeaveImportService.getLeaveRecords({
      ...this.buildCurrentAcademicTermFilterParams(),
      page: 1,
      limit: 1,
      sort: null,
      direction: null,
    }).subscribe({
      next: (response) => {
        if (response.total > 0) {
          this.importExistingDataConfirmDialog?.open();
          return;
        }

        this.openImportModal();
      },
      error: () => {
        this.importCheckErrorDialog?.open();
        this.isCheckingExistingData = false;
      },
      complete: () => {
        this.isCheckingExistingData = false;
      },
    });
  }

  onConfirmImportExistingData(): void {
    this.importExistingDataConfirmDialog?.close();
    setTimeout(() => this.openImportModal(), 250);
  }

  onImportFile(file: File): void {
    this.lastAction.set('import');

    if (this.isImporting) {
      return;
    }

    this.isImporting = true;
    this.erpLeaveImportService.importLeaveRecords(file).subscribe({
      next: () => {
        this.importForm?.clearFileSelection();
        this.importForm?.closeAfterImportSuccess(() => {
          this.importSuccessDialog?.open();
        });
        this.resetToDefaultAcademicTermFilters();
        this.loadLeaveRecords(1);
        if (this.activeTab() === 'match_results') {
          this.loadResults(1);
        }
      },
      error: (err) => {
        this.importForm?.showImportError(err.error);
        this.isImporting = false;
      },
      complete: () => {
        this.isImporting = false;
      },
    });
  }

  onTabChange(tab: ErpLeaveImportTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.refreshVisibleDatatables();

    if (tab === 'match_results') {
      this.loadResults(1);
      return;
    }

    this.loadLeaveRecords(1);
  }

  openTeacherDetail(row: ErpLeaveImportResultRow): void {
    if (this.isLoadingDetail) {
      return;
    }

    this.isLoadingDetail = true;
    this.isTeacherDetailTableReady.set(false);
    this.activeDetailStatusTab.set('approved');
    this.comparisonCurrentPage = 1;
    this.erpLeaveImportService.getResult(row.id).subscribe({
      next: (detail) => {
        this.selectedTeacherSummary = detail;
        this.comparisonTableDatasource = this.createComparisonTableDatasource(detail);
        this.cdr?.detectChanges();
        this.openTeacherDetailModal();
      },
      error: () => {
        this.isLoadingDetail = false;
      },
      complete: () => {
        this.isLoadingDetail = false;
      },
    });
  }

  onDetailStatusTabChange(tab: ErpLeaveImportDetailStatusTab): void {
    if (this.activeDetailStatusTab() === tab) {
      return;
    }

    this.activeDetailStatusTab.set(tab);
    this.comparisonCurrentPage = 1;
    this.comparisonTableDatasource = this.createComparisonTableDatasource(this.selectedTeacherSummary);
  }

  createComparisonTableDatasource(
    teacher?: ErpLeaveImportResultDetail,
  ): ErpLeaveImportComparisonDataSource {
    const rows = teacher == null ? [] : this.buildComparisonRows(teacher, this.activeDetailStatusTab());
    const limit = this.comparisonCurrentLimit;
    const totalPage = Math.max(Math.ceil(rows.length / limit), 1);
    const page = Math.min(Math.max(this.comparisonCurrentPage, 1), totalPage);
    const offset = (page - 1) * limit;
    const data = rows.slice(offset, offset + limit);
    this.comparisonCurrentPage = page;

    return {
      data,
      page,
      limit,
      total: rows.length,
      total_page: totalPage,
      offset,
    };
  }

  getUnsuccessfulComparisonCount(teacher: ErpLeaveImportResultDetail): number {
    return this.buildComparisonRows(teacher)
      .filter((row) => row.comparisonStatus !== 'matched')
      .length;
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.teacherTableDatasource.total_page || page === this.currentPage) {
      return;
    }

    this.loadResults(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadResults(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadResults(1);
  }

  onLeaveRecordPageChange(page: number): void {
    if (page < 1 || page > this.leaveRecordTableDatasource.total_page || page === this.leaveRecordCurrentPage) {
      return;
    }

    this.loadLeaveRecords(page);
  }

  onLeaveRecordLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.leaveRecordCurrentLimit) {
      return;
    }

    this.leaveRecordCurrentLimit = limit;
    this.loadLeaveRecords(1);
  }

  onLeaveRecordSortChange(event: DatatableSortChangeEvent): void {
    this.leaveRecordCurrentSortParams = createDatatableSortParams(event, this.leaveRecordSortFieldMap);
    this.loadLeaveRecords(1);
  }

  onComparisonPageChange(page: number): void {
    if (page < 1 || page > this.comparisonTableDatasource.total_page || page === this.comparisonCurrentPage) {
      return;
    }

    this.comparisonCurrentPage = page;
    this.comparisonTableDatasource = this.createComparisonTableDatasource(this.selectedTeacherSummary);
  }

  onComparisonLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.comparisonCurrentLimit) {
      return;
    }

    this.comparisonCurrentLimit = limit;
    this.comparisonCurrentPage = 1;
    this.comparisonTableDatasource = this.createComparisonTableDatasource(this.selectedTeacherSummary);
  }

  closeTeacherDetailModal(): void {
    const modalElement = document.getElementById('erpLeaveImportTeacherDetailModal');

    if (modalElement == null) {
      return;
    }

    bootstrap.Modal.getInstance(modalElement)?.hide();
  }

  closeImportSuccessDialog(): void {
    this.importSuccessDialog?.close();
    this.cleanupImportModalState();
    setTimeout(() => this.cleanupImportModalState(), 600);
  }

  formatDate(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const parts = value.split('-');
    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;
    return `${day}/${month}/${Number(year) + 543}`;
  }

  formatRequestTitle(request: ErpLeaveImportApprovedRequest): string {
    return [request.subject_code, request.subject_name].filter(Boolean).join(' - ') || '-';
  }

  formatRequestStatus(status: string | null | undefined): string {
    const labels: Record<string, string> = {
      approved: 'รับรองแล้ว',
      submitted: 'รอดำเนินการ',
      draft: 'รอดำเนินการ',
      rejected: 'ไม่อนุมัติ',
      revision_requested: 'ส่งกลับแก้ไข',
    };

    return status == null || status === '' ? '-' : labels[status] ?? status;
  }

  getRequestStatusBadgeClass(status: string | null | undefined): string {
    const classes: Record<string, string> = {
      approved: 'erp-leave-import-request-status-badge-success',
      submitted: 'erp-leave-import-request-status-badge-pending',
      draft: 'erp-leave-import-request-status-badge-pending',
      rejected: 'erp-leave-import-request-status-badge-danger',
      revision_requested: 'erp-leave-import-request-status-badge-revision',
    };

    return status == null || status === ''
      ? 'erp-leave-import-request-status-badge-neutral'
      : classes[status] ?? 'erp-leave-import-request-status-badge-neutral';
  }

  formatMakeupTime(request: ErpLeaveImportApprovedRequest): string {
    const date = this.formatDate(request.makeup_teaching_date);
    const timeRange = [request.makeup_start_time, request.makeup_end_time].filter(Boolean).join(' - ');

    return timeRange !== '' ? `${date} ${timeRange}` : date;
  }

  buildComparisonRows(
    teacher: ErpLeaveImportResultDetail,
    statusTab: ErpLeaveImportDetailStatusTab = this.activeDetailStatusTab(),
  ): ErpLeaveImportComparisonRow[] {
    const rows: Omit<ErpLeaveImportComparisonRow, 'sequence'>[] = [];
    const consumedErpRows = new Set<ErpLeaveImportRowResult>();

    if (statusTab === 'approved') {
      teacher.matched.forEach((erpRow) => {
        (erpRow.approved_requests ?? []).forEach((request) => {
          rows.push({
            id: `matched-${erpRow.leave_record_id ?? erpRow.schedule_submission_id}-${request.id}`,
            systemRequest: request,
            erpRequest: erpRow,
            systemLeaveDate: request.leave_date !== '' ? request.leave_date : null,
            sortDate: request.leave_date || erpRow.leave_date,
            comparisonStatus: 'matched',
            comparisonLabel: 'ตรงกัน',
          });
          consumedErpRows.add(erpRow);
        });
      });

      teacher.system_unmatched
        .filter((request) => this.isApprovedRequest(request))
        .forEach((request) => {
          rows.push({
            id: `system-${request.id}`,
            systemRequest: request,
            erpRequest: null,
            systemLeaveDate: request.leave_date !== '' ? request.leave_date : null,
            sortDate: request.leave_date !== '' ? request.leave_date : null,
            comparisonStatus: 'missing_erp',
            comparisonLabel: 'ไม่พบใน ERP',
          });
        });

      teacher.import_unmatched.forEach((erpRow) => {
        if (consumedErpRows.has(erpRow)) {
          return;
        }

        rows.push({
          id: `erp-${erpRow.leave_record_id ?? erpRow.schedule_submission_id}-${erpRow.leave_date}-${erpRow.row ?? 'db'}`,
          systemRequest: null,
          erpRequest: erpRow,
          systemLeaveDate: null,
          sortDate: erpRow.leave_date,
          comparisonStatus: 'missing_approved_request',
          comparisonLabel: 'ไม่พบคำขอในระบบ',
        });
      });

      return this.sequenceComparisonRows(rows);
    }

    teacher.system_unmatched
      .filter((request) => !this.isApprovedRequest(request))
      .forEach((request) => {
        rows.push({
          id: `pending-${request.id}`,
          systemRequest: request,
          erpRequest: null,
          systemLeaveDate: request.leave_date !== '' ? request.leave_date : null,
          sortDate: request.leave_date !== '' ? request.leave_date : null,
          comparisonStatus: 'not_approved',
          comparisonLabel: 'ยังไม่รับรอง',
        });
      });

    return this.sequenceComparisonRows(rows);
  }

  formatComparisonDateTime(row: ErpLeaveImportComparisonRow): string {
    const leaveDate = this.formatDate(row.systemLeaveDate);

    if (row.systemRequest == null) {
      return leaveDate;
    }

    const makeupTime = this.formatMakeupTime(row.systemRequest);
    return makeupTime !== '-' ? `${leaveDate}\n${makeupTime}` : leaveDate;
  }

  formatLeaveDate(row: ErpLeaveImportComparisonRow): string {
    return this.formatDate(row.systemLeaveDate);
  }

  formatErpLeaveDate(row: ErpLeaveImportComparisonRow): string {
    return this.formatErpDateRange(row.erpRequest);
  }

  formatErpDateRange(row: ErpLeaveImportRowResult | ErpLeaveImportLeaveRecordRow | null | undefined): string {
    const fallbackLeaveDate = row?.leave_date;
    const startValue = row?.erp_leave_start_date != null && row.erp_leave_start_date !== ''
      ? row.erp_leave_start_date
      : fallbackLeaveDate;
    const endValue = row?.erp_leave_end_date != null && row.erp_leave_end_date !== ''
      ? row.erp_leave_end_date
      : fallbackLeaveDate;
    const startDate = this.formatDate(startValue);
    const endDate = this.formatDate(endValue);

    if (startDate === '-' && endDate === '-') {
      return '-';
    }

    if (startDate === endDate || endDate === '-') {
      return startDate;
    }

    if (startDate === '-') {
      return endDate;
    }

    return `${startDate} - ${endDate}`;
  }

  formatMakeupTeachingDate(row: ErpLeaveImportComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    return this.formatDate(row.systemRequest.makeup_teaching_date);
  }

  formatMakeupTeachingTimeWithHours(row: ErpLeaveImportComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const timeRange = [row.systemRequest.makeup_start_time, row.systemRequest.makeup_end_time]
      .filter(Boolean)
      .join(' - ');

    if (timeRange === '') {
      return '-';
    }

    const hours = this.formatMakeupHours(row);
    return hours === '-' ? timeRange : `${timeRange} (${hours})`;
  }

  formatMakeupHours(row: ErpLeaveImportComparisonRow): string {
    if (row.systemRequest == null) {
      return '-';
    }

    const startMinutes = this.timeToMinutes(row.systemRequest.makeup_start_time);
    const endMinutes = this.timeToMinutes(row.systemRequest.makeup_end_time);

    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
      return '-';
    }

    return this.formatHours((endMinutes - startMinutes) / 60);
  }

  shouldShowMakeupTeaching(row: ErpLeaveImportComparisonRow): boolean {
    return row.systemRequest != null;
  }

  getComparisonBadgeClass(status: ErpLeaveImportComparisonStatus): string {
    const classes: Record<ErpLeaveImportComparisonStatus, string> = {
      matched: 'erp-leave-import-comparison-badge-success',
      missing_erp: 'erp-leave-import-comparison-badge-danger',
      not_approved: 'erp-leave-import-comparison-badge-danger',
      missing_approved_request: 'erp-leave-import-comparison-badge-danger',
    };

    return classes[status];
  }

  trackByImportRow(_index: number, row: ErpLeaveImportRowResult): string {
    return `${row.schedule_submission_id}-${row.leave_date}-${row.row}`;
  }

  trackByApprovedRequest(_index: number, request: ErpLeaveImportApprovedRequest): number {
    return request.id;
  }

  trackByComparisonRow(_index: number, row: ErpLeaveImportComparisonRow): string {
    return row.id;
  }

  formatDateTime(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543;
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  formatRowNumber(value: number | null | undefined): number | string {
    return value ?? '-';
  }

  formatIsActive(value: boolean | null | undefined): string {
    if (value === true) {
      return 'ใช้งาน';
    }

    if (value === false) {
      return 'ยกเลิก';
    }

    return '-';
  }

  getIsActiveBadgeClass(value: boolean | null | undefined): string {
    if (value === true) {
      return 'erp-leave-import-status-badge-active';
    }

    if (value === false) {
      return 'erp-leave-import-status-badge-inactive';
    }

    return 'erp-leave-import-status-badge-empty';
  }

  formatHours(value: number): string {
    const formattedValue = Number.isInteger(value)
      ? String(value)
      : value.toFixed(2).replace(/\.?0+$/, '');

    return `${formattedValue} ชม.`;
  }

  loadResults(page = this.currentPage): void {
    this.tableLoading.set(true);
    this.currentPage = page;

    this.erpLeaveImportService.getResults({
      page: this.currentPage,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).subscribe({
      next: (response) => {
        this.teacherTableDatasource = this.createTeacherTableDatasource(response);
        this.tableLoading.set(false);
        this.refreshVisibleDatatables();
      },
      error: () => {
        this.teacherTableDatasource = this.createTeacherTableDatasource();
        this.tableLoading.set(false);
        this.refreshVisibleDatatables();
      },
    });
  }

  loadLeaveRecords(page = this.leaveRecordCurrentPage): void {
    this.leaveRecordTableLoading.set(true);
    this.leaveRecordCurrentPage = page;

    this.erpLeaveImportService.getLeaveRecords({
      ...this.buildLeaveRecordFilterParams(),
      page: this.leaveRecordCurrentPage,
      limit: this.leaveRecordCurrentLimit,
      sort: this.leaveRecordCurrentSortParams.sort,
      direction: this.leaveRecordCurrentSortParams.direction,
    }).subscribe({
      next: (response) => {
        this.leaveRecordTableDatasource = this.createLeaveRecordTableDatasource(response);
        this.leaveRecordTableLoading.set(false);
        this.refreshVisibleDatatables();
      },
      error: () => {
        this.leaveRecordTableDatasource = this.createLeaveRecordTableDatasource();
        this.leaveRecordTableLoading.set(false);
        this.refreshVisibleDatatables();
      },
    });
  }

  private refreshVisibleDatatables(): void {
    [0, 100].forEach((delay) => {
      const timer = setTimeout(() => {
        this.tableRefreshTimers.delete(timer);
        this.datatables?.forEach((datatable) => datatable.refreshLayout());
      }, delay);

      this.tableRefreshTimers.add(timer);
    });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
        cellClass: 'text-center',
        headerClass: 'd-flex justify-content-center',
      },
      {
        prop: 'teacher',
        name: 'ชื่อ-นามสกุล อาจารย์ / อีเมล',
        sortable: true,
        cellTemplate: this.teacherInfoTemplate,
        width: 320,
        minWidth: 260,
      },
      {
        prop: 'system_matched_request_count',
        name: 'รายการคำขอที่ตรงกัน',
        sortable: true,
        cellTemplate: this.matchCountTemplate,
        width: 140,
        minWidth: 130,
      },
      {
        prop: 'system_unmatched_request_count',
        name: 'รายการคำขอที่ไม่ตรงกัน',
        sortable: true,
        cellTemplate: this.systemRequestUnmatchedCountTemplate,
        width: 170,
        minWidth: 160,
      },
      {
        prop: 'system_request_count',
        name: 'รายการคำขอทั้งหมด',
        sortable: true,
        cellTemplate: this.systemRequestCountTemplate,
        width: 170,
        minWidth: 160,
      },
      {
        prop: 'imported_at',
        name: 'วันที่นำเข้าล่าสุด',
        sortable: true,
        cellTemplate: this.importedAtTemplate,
        width: 180,
        minWidth: 170,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.teacherActionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center erp-leave-import-action-cell',
        headerClass: 'text-center erp-leave-import-action-header',
      },
    ];
  }

  private initLeaveRecordColumns(): void {
    this.leaveRecordColumns = [
      {
        prop: 'sequence',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
        cellClass: 'text-center',
        headerClass: 'd-flex justify-content-center',
      },
      {
        prop: 'erp_person_number',
        name: 'PERSON_NUMBER',
        sortable: true,
        width: 150,
        minWidth: 150,
        cellClass: 'erp-leave-import-text-cell',
        headerClass: 'erp-leave-import-text-header',
      },
      {
        prop: 'erp_person_name',
        name: 'PERSON_NAME',
        sortable: true,
        width: 220,
        minWidth: 220,
        cellClass: 'erp-leave-import-text-cell',
        headerClass: 'erp-leave-import-text-header',
      },
      {
        prop: 'erp_full_name',
        name: 'FULL_NAME',
        sortable: true,
        width: 220,
        minWidth: 220,
        cellClass: 'erp-leave-import-text-cell',
        headerClass: 'erp-leave-import-text-header',
      },
      {
        prop: 'erp_department',
        name: 'DEPARTMENT',
        sortable: true,
        width: 300,
        minWidth: 300,
        cellClass: 'erp-leave-import-text-cell',
        headerClass: 'erp-leave-import-text-header',
      },
      {
        prop: 'leave_type',
        name: 'LEAVE_TYPE',
        sortable: true,
        cellTemplate: this.leaveTypeTemplate,
        width: 220,
        minWidth: 220,
        cellClass: 'erp-leave-import-text-cell',
        headerClass: 'erp-leave-import-text-header',
      },
      {
        prop: 'erp_leave_start_date',
        name: 'LEAVE_START_DATE',
        sortable: true,
        cellTemplate: this.erpLeaveStartDateTemplate,
        width: 170,
        minWidth: 170,
        cellClass: 'text-center erp-leave-import-center-cell',
        headerClass: 'text-center erp-leave-import-center-header',
      },
      {
        prop: 'erp_leave_end_date',
        name: 'LEAVE_END_DATE',
        sortable: true,
        cellTemplate: this.erpLeaveEndDateTemplate,
        width: 160,
        minWidth: 160,
        cellClass: 'text-center erp-leave-import-center-cell',
        headerClass: 'text-center erp-leave-import-center-header',
      },
      {
        prop: 'erp_submitted_at',
        name: 'SUBMITTED_DATE',
        sortable: true,
        cellTemplate: this.erpSubmittedAtTemplate,
        width: 210,
        minWidth: 210,
        cellClass: 'text-center erp-leave-import-center-cell',
        headerClass: 'text-center erp-leave-import-center-header',
      },
      {
        prop: 'erp_last_updated_at',
        name: 'LAST_UPDATED_DATE',
        sortable: true,
        cellTemplate: this.erpLastUpdatedAtTemplate,
        width: 210,
        minWidth: 210,
        cellClass: 'text-center erp-leave-import-center-cell',
        headerClass: 'text-center erp-leave-import-center-header',
      },
      {
        prop: 'erp_approval_status',
        name: 'APPROVAL_STATUS',
        sortable: true,
        width: 180,
        minWidth: 180,
        cellClass: 'erp-leave-import-text-cell',
        headerClass: 'erp-leave-import-text-header',
      },
      {
        prop: 'erp_adjustment_status',
        name: 'ADJUSTMENT_STATUS',
        sortable: true,
        width: 200,
        minWidth: 200,
        cellClass: 'erp-leave-import-text-cell',
        headerClass: 'erp-leave-import-text-header',
      },
    ];
  }

  private initComparisonColumns(): void {
    this.comparisonColumns = [
      {
        prop: 'sequence',
        name: '#',
        sortable: false,
        cellTemplate: this.comparisonIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        cellClass: 'text-center erp-leave-import-center-cell',
        headerClass: 'text-center erp-leave-import-center-header',
      },
      {
        prop: 'systemRequest',
        name: 'คำขอชดเชยจากระบบ',
        sortable: false,
        cellTemplate: this.comparisonSystemRequestTemplate,
        width: 330,
        minWidth: 300,
        flexGrow: 2,
        cellClass: 'erp-leave-import-comparison-detail-cell',
        headerClass: 'erp-leave-import-text-header',
      },
     
      {
        prop: 'systemLeaveDate',
        name: 'วันที่ขอลา/หยุด',
        sortable: false,
        cellTemplate: this.comparisonLeaveDateTemplate,
        width: 132,
        minWidth: 120,
        flexGrow: 1,
        cellClass: 'text-center erp-leave-import-center-cell',
        headerClass: 'text-center erp-leave-import-center-header',
      },
      {
        prop: 'makeupTeachingDate',
        name: 'วันขอสอนชดเชย',
        sortable: false,
        cellTemplate: this.comparisonMakeupTeachingTemplate,
        width: 250,
        minWidth: 220,
        flexGrow: 1,
        cellClass: 'text-center erp-leave-import-center-cell',
        headerClass: 'text-center erp-leave-import-center-header',
      },
       {
        prop: 'erpRequest',
        name: 'วันที่ขอลา/หยุด จาก ERP',
        sortable: false,
        cellTemplate: this.comparisonErpRequestTemplate,
        width: 170,
        minWidth: 160,
        flexGrow: 1,
        cellClass: 'erp-leave-import-comparison-detail-cell',
        headerClass: 'erp-leave-import-text-header',
      },
      {
        prop: 'comparisonStatus',
        name: 'ผลการเปรียบเทียบ',
        sortable: false,
        cellTemplate: this.comparisonStatusTemplate,
        width: 170,
        minWidth: 160,
        flexGrow: 1,
        cellClass: 'text-center erp-leave-import-center-cell',
        headerClass: 'text-center erp-leave-import-center-header',
      },
    ];
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
    });
  }

  private compareComparisonRows(
    left: Omit<ErpLeaveImportComparisonRow, 'sequence'>,
    right: Omit<ErpLeaveImportComparisonRow, 'sequence'>,
  ): number {
    const leftStatusOrder = this.comparisonStatusSortOrder(left.comparisonStatus);
    const rightStatusOrder = this.comparisonStatusSortOrder(right.comparisonStatus);

    if (leftStatusOrder !== rightStatusOrder) {
      return leftStatusOrder - rightStatusOrder;
    }

    const leftDate = left.sortDate ?? '';
    const rightDate = right.sortDate ?? '';

    if (leftDate !== rightDate) {
      return leftDate.localeCompare(rightDate);
    }

    return left.id.localeCompare(right.id);
  }

  private sequenceComparisonRows(
    rows: Omit<ErpLeaveImportComparisonRow, 'sequence'>[],
  ): ErpLeaveImportComparisonRow[] {
    return rows
      .sort((left, right) => this.compareComparisonRows(left, right))
      .map((row, index) => ({
        ...row,
        sequence: index + 1,
      }));
  }

  private isApprovedRequest(request: ErpLeaveImportApprovedRequest): boolean {
    return request.status === 'approved';
  }

  private comparisonStatusSortOrder(status: ErpLeaveImportComparisonStatus): number {
    const order: Record<ErpLeaveImportComparisonStatus, number> = {
      matched: 0,
      missing_approved_request: 1,
      missing_erp: 2,
      not_approved: 3,
    };

    return order[status];
  }

  private listenLeaveRecordFilters(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadLeaveRecords(1));
  }

  private loadAcademicTermOptions(): void {
    this.studyPeriodService.getAcademicTermYears()
      .pipe(take(1))
      .subscribe({
        next: (terms) => {
          this.academicTerms = terms;
          const academicTermOptions = this.buildAcademicTermOptions(terms);
          const yearOptions = this.buildAcademicYearOptions(terms);

          this.academicTermOptions = academicTermOptions.length > 0 ? academicTermOptions : this.academicTermOptions;
          this.yearOptions = yearOptions.length > 0 ? yearOptions : this.yearOptions;
          this.ensureAcademicTermFilterValues();
          this.loadLeaveRecords(1);
        },
        error: () => this.loadLeaveRecords(1),
      });
  }

  private buildLeaveRecordFilterParams(): Pick<
    ErpLeaveImportLeaveRecordsParams,
    'academic_term_id' | 'academic_term_name' | 'academic_term_year'
  > {
    const { academicTerm, year } = this.form.getRawValue();
    const normalizedAcademicTerm = String(academicTerm ?? '').trim();
    const normalizedYear = String(year ?? '').trim();
    const selectedTerm = this.findAcademicTerm(normalizedAcademicTerm, normalizedYear);
    const params: Pick<
      ErpLeaveImportLeaveRecordsParams,
      'academic_term_id' | 'academic_term_name' | 'academic_term_year'
    > = {};

    if (selectedTerm != null) {
      params.academic_term_id = selectedTerm.id;
      return params;
    }

    if (normalizedAcademicTerm !== '') {
      params.academic_term_name = normalizedAcademicTerm;
    }

    const numericYear = Number(normalizedYear);
    if (Number.isFinite(numericYear) && numericYear > 0) {
      params.academic_term_year = this.toChristianYear(numericYear);
    }

    return params;
  }

  private buildCurrentAcademicTermFilterParams(): Pick<
    ErpLeaveImportLeaveRecordsParams,
    'academic_term_id' | 'academic_term_name' | 'academic_term_year'
  > {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();
    const currentTerm = this.findAcademicTerm(defaultAcademicTerm, defaultYear);

    if (currentTerm != null) {
      return { academic_term_id: currentTerm.id };
    }

    return {
      academic_term_name: defaultAcademicTerm,
      academic_term_year: this.toChristianYear(Number(defaultYear)),
    };
  }

  private resetToDefaultAcademicTermFilters(): void {
    this.form.patchValue({
      academicTerm: this.getDefaultAcademicTermName(),
      year: this.getDefaultBuddhistYear(),
    }, { emitEvent: false });
  }

  private ensureAcademicTermFilterValues(): void {
    const currentAcademicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const currentYear = String(this.form.get('year')?.value ?? '');

    if (this.findAcademicTerm(currentAcademicTerm, currentYear) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === academicTermName &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private toBuddhistYear(year: number): number {
    return year > 2400 ? year : year + 543;
  }

  private toChristianYear(year: number): number {
    return year > 2400 ? year - 543 : year;
  }

  private timeToMinutes(value: string | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const [hour, minute] = value.split(':').map((part) => Number(part));

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      return null;
    }

    return (hour * 60) + minute;
  }

  private createTeacherTableDatasource(response?: ErpLeaveImportResultsResponse): ErpLeaveImportTeacherDataSource {
    return {
      data: response?.data ?? [],
      limit: response?.limit ?? this.currentLimit,
      offset: response?.offset ?? ((response?.page ?? 1) - 1) * (response?.limit ?? this.currentLimit),
      page: response?.page ?? 1,
      total: response?.total ?? 0,
      total_page: response?.total_page ?? response?.total_pages ?? 1,
    };
  }

  private createLeaveRecordTableDatasource(response?: ErpLeaveImportLeaveRecordsResponse): ErpLeaveImportLeaveRecordDataSource {
    return {
      data: response?.data ?? [],
      limit: response?.limit ?? this.leaveRecordCurrentLimit,
      offset: response?.offset ?? ((response?.page ?? 1) - 1) * (response?.limit ?? this.leaveRecordCurrentLimit),
      page: response?.page ?? 1,
      total: response?.total ?? 0,
      total_page: response?.total_page ?? response?.total_pages ?? 1,
    };
  }

  private openTeacherDetailModal(): void {
    const modalElement = document.getElementById('erpLeaveImportTeacherDetailModal');

    if (modalElement == null) {
      return;
    }

    modalElement.addEventListener('shown.bs.modal', () => {
      this.isTeacherDetailTableReady.set(true);

      if (this.selectedTeacherSummary != null) {
        this.comparisonTableDatasource = this.createComparisonTableDatasource(this.selectedTeacherSummary);
      }

      this.cdr?.detectChanges();
    }, { once: true });

    modalElement.addEventListener('hidden.bs.modal', () => {
      this.isTeacherDetailTableReady.set(false);
      this.cdr?.detectChanges();
    }, { once: true });

    bootstrap.Modal.getOrCreateInstance(modalElement, {
      backdrop: 'static',
      keyboard: false,
    }).show();
  }

  private openImportModal(): void {
    this.importForm?.openModal();
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    window.URL.revokeObjectURL(url);
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const responseError = (error as { error?: unknown } | null | undefined)?.error;

    if (responseError != null && typeof responseError === 'object') {
      const errorText = (responseError as { error?: unknown; message?: unknown }).error;
      const messageText = (responseError as { error?: unknown; message?: unknown }).message;
      const message = this.getText(errorText) || this.getText(messageText);

      return message || fallback;
    }

    return this.getText(responseError) || this.getText((error as { message?: unknown } | null | undefined)?.message) || fallback;
  }

  private getText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private cleanupImportModalState(): void {
    [
      'erpLeaveImportDataModal',
      'erpLeaveImportDataModalLoading',
      'erpLeaveImportDataModalError',
      'erpLeaveImportSuccess',
    ].forEach((modalId) => this.forceHideModal(modalId));

    if (document.querySelector('.modal.show') != null) {
      return;
    }

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  }

  private forceHideModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);

    if (modalElement == null) {
      return;
    }

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && modalElement.contains(activeElement)) {
      activeElement.blur();
    }

    bootstrap.Modal.getInstance(modalElement)?.hide();
    modalElement.classList.remove('show');
    modalElement.setAttribute('aria-hidden', 'true');
    modalElement.removeAttribute('aria-modal');
    modalElement.removeAttribute('role');
    modalElement.style.display = 'none';
  }
}
